import { loadSwiper } from './swiper-loader.js';

if (!customElements.get('featured-collections')) {
    class FeaturedCollections extends HTMLElement {
        #loadToken = 0;
        #panelLoads = new Map();

        constructor() {
            super();

            this.swipers = {};
            this.currentSwiper = null;
            this.focusIndex = 0;
        }

        connectedCallback() {
            this.init();
        }

        disconnectedCallback() {
            window.removeEventListener('resize', this.#onResize);
            this.#destroySwipers();
        }

        init() {
            this.#destroySwipers();

            this.elTabPanels = this.querySelectorAll('[data-featured-collections-tabpanel]');
            this.elTabs = this.querySelectorAll('[data-featured-collection-tab]');
            this.elNav = this.querySelectorAll('[data-featured-collections-nav]');
            this.elStatus = this.querySelector('[data-featured-collections-status]');

            if (!this.elTabPanels.length) return;

            this.elNav.forEach(el => el.addEventListener('click', this.navigateCarousel.bind(this)));
            window.addEventListener('resize', this.#onResize);

            this.#setupPanels();
            this.#bindTabs();
            this.#initVisibleCarousel();
        }

        #onResize = () => {
            this.#updateNavState();
        };

        #destroySwipers() {
            for (const swiper of Object.values(this.swipers)) {
                swiper?.destroy?.(true, true);
            }

            this.swipers = {};
            this.currentSwiper = null;

            for (const el of this.querySelectorAll('[data-featured-collection-carousel]')) {
                el.removeAttribute('data-swiper-ready');
            }
        }

        #setupPanels() {
            this.elTabPanels.forEach((el, index) => {
                el.toggleAttribute('hidden', index !== 0);
                el.tabIndex = index === 0 ? 0 : -1;
            });
        }

        #bindTabs() {
            this.elTabs.forEach(el => {
                el.addEventListener('click', this.setActiveTab.bind(this));
                el.addEventListener('keydown', this.#handleKeydown.bind(this));
            });
        }

        async #initVisibleCarousel() {
            const firstPanel = this.elTabPanels[0];
            const firstCarousel = firstPanel?.querySelector('[data-featured-collection-carousel]');

            if (!firstCarousel) return;

            await this.#initCarousel(firstCarousel);
            this.currentSwiper = this.swipers[firstCarousel.id] || null;
            this.#updateNavState();
        }

        async #initCarousel(el) {
            if (!el || this.swipers[el.id]) {
                this.swipers[el.id]?.update();
                return;
            }

            const prevSlideMessage = this.dataset.prevSlideMessage || 'Previous slide';
            const nextSlideMessage = this.dataset.nextSlideMessage || 'Next slide';
            const Swiper = await loadSwiper();

            const swiper = new Swiper(el, {
                loop: false,
                watchOverflow: true,
                slidesPerView: 'auto',
                slidesPerGroup: 1,
                spaceBetween: 8,
                slidesOffsetBefore: 12,
                slidesOffsetAfter: 12,
                autoplay: false,
                a11y: {
                    enabled: true,
                    prevSlideMessage,
                    nextSlideMessage,
                },
                breakpoints: {
                    768: {
                        slidesPerView: 4,
                        slidesPerGroup: 4,
                        spaceBetween: 12,
                        slidesOffsetBefore: 0,
                        slidesOffsetAfter: 0
                    }
                },
                on: {
                    init: (instance) => this.#onSwiperUpdate(instance),
                    slideChange: (instance) => this.#onSwiperUpdate(instance),
                    breakpoint: (instance) => this.#onSwiperUpdate(instance),
                }
            });

            this.swipers[el.id] = swiper;
            el.setAttribute('data-swiper-ready', '');
        }

        #handleKeydown(event) {
            const tabs = [...this.elTabs];
            const currentIndex = tabs.indexOf(event.currentTarget);

            const nextIndex = (() => {
                switch (event.key) {
                    case 'ArrowRight': return (currentIndex + 1) % tabs.length;
                    case 'ArrowLeft': return (currentIndex - 1 + tabs.length) % tabs.length;
                    case 'Home': return 0;
                    case 'End': return tabs.length - 1;
                    default: return -1;
                }
            })();

            if (nextIndex < 0) return;

            event.preventDefault();
            const nextTab = tabs[nextIndex];
            nextTab.focus({ preventScroll: true });
            this.#activateTab(nextTab);
        }

        #getSwiperForPanel(panel) {
            const carousel = panel.querySelector('[data-featured-collection-carousel]');
            return carousel ? this.swipers[carousel.id] : null;
        }

        navigateCarousel({ currentTarget }) {
            if (!this.currentSwiper) return;

            const isNext = currentTarget.getAttribute('data-featured-collections-nav') === 'next';
            isNext ? this.currentSwiper.slideNext() : this.currentSwiper.slidePrev();
        }

        #onSwiperUpdate(swiper) {
            if (swiper === this.currentSwiper) {
                this.#updateNavState();
            }
        }

        #updateNavState() {
            if (!this.currentSwiper) return;

            this.elNav.forEach(el => {
                const direction = el.getAttribute('data-featured-collections-nav');

                if (direction === 'prev') {
                    el.disabled = this.currentSwiper.isBeginning;
                } else if (direction === 'next') {
                    el.disabled = this.currentSwiper.isEnd;
                }
            });
        }

        setActiveTab({ currentTarget }) {
            this.#activateTab(currentTarget);
        }

        async #activateTab(elTab) {
            const activePanelId = elTab.getAttribute('aria-controls');
            const nextPanel = this.querySelector(`#${CSS.escape(activePanelId)}`);

            if (!nextPanel) return;

            const isAlreadyActive = elTab.getAttribute('aria-selected') === 'true';
            const needsFetch = nextPanel.hasAttribute('data-tab-pending');

            if (isAlreadyActive && !needsFetch) {
                return;
            }

            const previousTab = this.elTabs[this.focusIndex] || this.elTabs[0];
            this.#syncTabButtons(elTab);

            if (!needsFetch) {
                const carousel = nextPanel.querySelector('[data-featured-collection-carousel]');

                if (carousel) {
                    await this.#initCarousel(carousel);
                }

                this.#showPanel(nextPanel);
                this.focusIndex = [...this.elTabs].indexOf(elTab);
                return;
            }

            this.classList.add('loading');
            this.setAttribute('aria-busy', 'true');
            this.#announce(this.dataset.labelLoading || 'Loading products');

            const token = ++this.#loadToken;

            try {
                if (nextPanel.hasAttribute('data-tab-pending')) {
                    await this.#ensurePanelLoaded(nextPanel);
                }

                if (token !== this.#loadToken) return;

                const carousel = nextPanel.querySelector('[data-featured-collection-carousel]');
                if (carousel) {
                    await this.#initCarousel(carousel);
                }

                if (token !== this.#loadToken) return;

                this.#showPanel(nextPanel);
                this.focusIndex = [...this.elTabs].indexOf(elTab);
                this.#announce('');
            } catch (error) {
                if (token !== this.#loadToken) return;

                console.error('featured-collections tab load failed', error);
                this.#syncTabButtons(previousTab);
                this.#announce(this.dataset.labelError || 'Unable to load products. Try again.');
            } finally {
                if (token === this.#loadToken) {
                    this.classList.remove('loading');
                    this.removeAttribute('aria-busy');
                }
            }
        }

        #syncTabButtons(elTab) {
            this.elTabs.forEach((el, index) => {
                const isActive = el === elTab;
                el.classList.toggle('active', isActive);
                el.setAttribute('aria-selected', isActive ? 'true' : 'false');
                el.setAttribute('tabindex', isActive ? '0' : '-1');

                if (isActive) {
                    this.focusIndex = index;
                }
            });
        }

        #showPanel(nextPanel) {
            this.elTabPanels.forEach((el) => {
                const isActive = el === nextPanel;

                el.toggleAttribute('hidden', !isActive);
                el.tabIndex = isActive ? 0 : -1;

                if (isActive) {
                    this.currentSwiper = this.#getSwiperForPanel(el);
                    this.currentSwiper?.update();
                    this.#updateNavState();
                }
            });
        }

        #ensurePanelLoaded(panel) {
            const panelId = panel.id;

            if (this.#panelLoads.has(panelId)) {
                return this.#panelLoads.get(panelId);
            }

            const load = this.#fetchPanel(panel)
                .then(() => {
                    panel.removeAttribute('data-tab-pending');
                    window.initLazyLoad?.();
                })
                .catch((error) => {
                    this.#panelLoads.delete(panelId);
                    throw error;
                });

            this.#panelLoads.set(panelId, load);
            return load;
        }

        async #fetchPanel(panel) {
            const url = new URL(window.location.pathname, window.location.origin);
            url.searchParams.set('section_id', this.id);
            url.searchParams.set('featured_tab', panel.id);

            const response = await fetch(url.toString());

            if (!response.ok) {
                throw new Error(`Failed to load featured collection tab: ${response.status}`);
            }

            const html = await response.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const remotePanel = doc.getElementById(panel.id);
            const remoteWrapper = remotePanel?.querySelector('.swiper-wrapper');
            const localWrapper = panel.querySelector('.swiper-wrapper');

            if (!remoteWrapper || !localWrapper) {
                throw new Error('Featured collection tab markup missing from response');
            }

            localWrapper.replaceChildren(...remoteWrapper.children);
        }

        #announce(message) {
            if (!this.elStatus) return;
            this.elStatus.textContent = message;
        }
    }

    customElements.define('featured-collections', FeaturedCollections);
}
